import { DatePipe } from '@angular/common';
import { FilterAllOptions, FilterBubbleDateLabel, FilterFormDataModel, SpDateFormatPipe } from '@thehubz/client/core';
import { AnyType, AssetMetadata } from '@thehubz/shared/model';

export interface FilterBubbleModel {
  _id: string;
  name: string;
  key: string;
}

const LABEL_TEXT = $localize`Text` + ': ';
const LABEL_TAG = $localize`Tag` + ': ';

export class FilterBubblesUtil {
  private filterBubbles: FilterBubbleModel[] = [];

  constructor(
    private datePipe: DatePipe,
    private spDatePipe: SpDateFormatPipe,
  ) {}

  get getFilterBubbles() {
    return this.filterBubbles;
  }

  /**
   * Create a array of object for filter bubbles
   * @returns
   */
  createFilterBubbles(filterAssetFormData: FilterFormDataModel, selectedFilterObject?: Record<string, AnyType>): FilterBubbleModel[] {
    if (!selectedFilterObject || Object.keys(selectedFilterObject).length === 0) {
      return [];
    }

    this.filterBubbles = Object.keys(selectedFilterObject)
      .map((key) => {
        const filterFormField = filterAssetFormData?.formFields;
        const fieldIndex = filterFormField?.findIndex((field) => field.formFieldName === key);
        const fieldLabel = fieldIndex !== -1 ? filterAssetFormData?.formFields[fieldIndex].fieldLabel : '';
        if (fieldIndex !== -1 && filterAssetFormData?.formFields[fieldIndex].multiple) {
          return selectedFilterObject[key].map((obj: AnyType) => ({ _id: obj._id || obj.id || obj, name: `${fieldLabel}: ${obj.name || obj}`, key }));
        } else {
          switch (true) {
            case key === 'expiryDate':
            case key === 'createdAt': {
              return {
                _id: key,
                name: `${fieldLabel}: ${this.spDatePipe.transform(selectedFilterObject[key].toString())}`,
                key,
              };
            }
            case key === 'createdDate_from':
            case key === 'createdDate_to':
            case key === 'expiryDate_from':
            case key === 'expiryDate_to':
            case key === 'plannedForDeletion_from':
            case key === 'plannedForDeletion_to':
            case key === 'publishedDate_until':
            case key === 'publishedDate_from': {
              const dateLabel = FilterBubbleDateLabel[key];
              return {
                _id: key,
                name: `${dateLabel}: ${this.datePipe.transform(selectedFilterObject[key].toString(), 'dd.MM.yyyy')}`,
                key,
              };
            }
            case key === AssetMetadata.General.TAGS: {
              return selectedFilterObject[key].map((obj: AnyType) => ({ _id: obj._id, name: LABEL_TAG + obj.name, key }));
            }
            case key.startsWith('searchText'): {
              return {
                _id: selectedFilterObject[key].name,
                name: LABEL_TEXT + selectedFilterObject[key].name,
                key,
              };
            }
            default: {
              return {
                _id: key,
                name: `${fieldLabel}: ${selectedFilterObject[key].name || selectedFilterObject[key]}`,
                key,
              };
            }
          }
        }
      })
      .flat(1); // added this flat because tags key value is coming as array
    return this.filterBubbles;
  }

  /**
   * Remove single filter bubble
   * @param filterBubble
   */
  removeFilterBubble(filterBubble: FilterBubbleModel, selectedFilterObject: Record<string, AnyType>, filterObject: FilterAllOptions) {
    const filterBubblesIndex = this.filterBubbles.findIndex((filter) => filter._id === filterBubble._id);
    if (filterBubblesIndex !== -1) {
      switch (true) {
        case filterBubble.key === 'client': {
          if ('brand' in selectedFilterObject) {
            const brandIndex = this.filterBubbles.findIndex((filter) => filter._id === 'brand');
            delete selectedFilterObject['brand'];
            this.filterBubbles.splice(brandIndex, 1);
          }
          delete selectedFilterObject[filterBubble._id];
          this.filterBubbles.splice(filterBubblesIndex, 1);
          break;
        }
        case filterBubble.key === AssetMetadata.General.TAGS:
        case filterBubble.key === 'alignment':
        case filterBubble.key === 'ageGroup':
        case filterBubble.key === 'groupOfPeople': {
          const filterIndex = selectedFilterObject[filterBubble.key]?.findIndex((key: AnyType) => key._id === filterBubble._id);
          if (filterIndex !== -1) {
            this.filterBubbles.splice(filterBubblesIndex, 1);
            selectedFilterObject[filterBubble.key].splice(filterIndex, 1);
            if (selectedFilterObject[filterBubble.key].length === 0) delete selectedFilterObject[filterBubble.key];
          }
          return;
        }
        case filterBubble.key.startsWith('searchText'): {
          this.filterBubbles.splice(filterBubblesIndex, 1);
          delete selectedFilterObject[filterBubble.key];
          break;
        }
        default: {
          this.filterBubbles.splice(filterBubblesIndex, 1);
          if (Array.isArray(selectedFilterObject[filterBubble.key])) {
            const filterIndex = selectedFilterObject[filterBubble.key].findIndex((filter: AnyType) => (filter.id || filter._id || filter) === filterBubble._id);
            if (filterIndex !== -1) selectedFilterObject[filterBubble.key].splice(filterIndex, 1);
            if (selectedFilterObject[filterBubble.key].length === 0) delete selectedFilterObject[filterBubble.key];
          } else {
            delete selectedFilterObject[filterBubble._id];
          }
          if (filterObject.filters['customFields.' + filterBubble.key]) {
            if (
              filterObject.filters['customFields.' + filterBubble.key] &&
              filterObject.filters['customFields.' + filterBubble.key]['$in'] &&
              filterObject.filters['customFields.' + filterBubble.key]['$in'].length == 1
            ) {
              delete filterObject.filters['customFields.' + filterBubble.key];
            }
          }
        }
      }
    }
    return this.filterBubbles;
  }

  /**
   * Clear all filter bubbles
   */
  removeAllFilterBubble() {
    this.filterBubbles = [];
  }
}
